import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import { app, authentication } from '@microsoft/teams-js';

import { logger } from '../logger.js';
import {
  getState,
  setIdentity,
  setPlatformJwt,
} from '../store/identity-store.js';
import {
  getRuntimeConfig,
  loadRuntimeConfig,
  runtimeConfigFetched,
} from './runtime-config.js';
import {
  _resetServerClockForTests,
  rememberServerClock,
  serverNow,
} from './server-clock.js';
import { isRunningInTeams } from './teams-env.js';
import { isTeamsAppSuspended, whenTeamsResumed } from './teams-suspension.js';
import { withTimeout } from './with-timeout.js';

/**
 * Sign-in → platform JWT.
 *
 * Every path ends the same way: a credential lands at cti-identity-service
 * (`POST /v1/auth/token`, public on the gateway) and comes back as a platform
 * JWT — the bearer for every `/v1` call, verified by the gateway and each
 * service against identity's JWKS.
 *
 *  - **Teams Tab** — `signInWithTeamsSso()`: Teams SDK `getAuthToken()`
 *    (audience = the Entra app from the Teams manifest's `webApplicationInfo`)
 *    → `grant_type: teams_sso`. Non-interactive; AuthGate runs it on boot and
 *    `ensureFreshPlatformJwt()` re-runs it silently when the JWT expires.
 *  - **Standalone browser, Entra configured** (runtime config `sso.msalClientId`) —
 *    `signInWithMicrosoft()`: MSAL popup → Entra ID token → `grant_type: entra`.
 *    `ensureFreshPlatformJwt()` renews it silently against Entra (no popup).
 *  - **Standalone browser, local/dev** — `signInWithEmail()` → `grant_type: dev`.
 *
 * The platform JWT lives 15 min, because that TTL is also the revocation window,
 * so every host renews silently and every renewal goes back to Entra — that is
 * what keeps the directory claims a session carries current.
 */
export function ssoConfigured(): boolean {
  return Boolean(getRuntimeConfig().sso.msalClientId);
}

export interface SsoUser {
  id: string;
  email: string;
  name?: string;
  tenant_id?: string;
  graph_search_notice_required?: boolean;
  roles?: string[];
}

/** Parsed `POST /v1/auth/token` response — the platform JWT plus the user it identifies. */
export interface PlatformSession {
  token: string;
  user: SsoUser;
}

/**
 * Identity refused this account a session — the access gate said no, or the
 * account is suspended or gone. A settled verdict, not a transient failure —
 * the credential was valid, the person is simply not allowed in — so callers
 * show the denial screen instead of a network-error retry.
 *
 * The message is the same for every cause on purpose: the screen must not say
 * which check failed.
 */
export class AccessDeniedError extends Error {
  constructor() {
    super('Access to this application is not granted for your account');
    this.name = 'AccessDeniedError';
  }
}

/**
 * The denial, once identity has stated it. Every exchange in this tab
 * short-circuits on it, which is what keeps a refusal from becoming a sign-in
 * loop: AuthGate re-runs its Teams bootstrap on every render without a JWT and
 * `ensureFreshPlatformJwt()` runs on every API call, so an unlatched denial
 * would re-post `/v1/auth/token` forever. Only a reload clears it — a
 * membership change needs a fresh token from Entra anyway.
 */
let accessDenied: AccessDeniedError | null = null;

/** Matches TEAMS_BOOT_TIMEOUT_MS in identity.ts — Teams desktop's getAuthToken
 *  can take several seconds on a cold client; 2 s races reject too early. */
const TEAMS_SSO_TIMEOUT_MS = 8000;

/** The scopes every Entra call here asks for — `openid` is what makes the response carry an ID token. */
const MSAL_SCOPES = ['openid', 'profile', 'email'];

const DEFAULT_AUTHORITY = 'https://login.microsoftonline.com/common';

/**
 * MSAL, created once per tab. It has to be a module-level singleton: the popup
 * sign-in and every later silent renewal must share one account cache, and a
 * per-call instance would drop that cache the moment sign-in returned. The
 * cache lives in localStorage so the account survives a closed-and-reopened tab
 * and renewal still works without another popup.
 */
let msalReady: Promise<PublicClientApplication> | null = null;

/** Builds and initialises MSAL. Kept apart from the caching so a failed attempt is retryable. */
async function createMsalInstance(): Promise<PublicClientApplication> {
  const { msalClientId: clientId, msalAuthority } = getRuntimeConfig().sso;
  if (!clientId) throw new Error('Microsoft SSO is not configured');
  const { PublicClientApplication: MsalPublicClientApplication } =
    await import('@azure/msal-browser');
  const instance = new MsalPublicClientApplication({
    auth: {
      clientId,
      authority: msalAuthority || DEFAULT_AUTHORITY,
      redirectUri:
        getRuntimeConfig().sso.msalRedirectUri || window.location.origin,
    },
    cache: { cacheLocation: 'localStorage', storeAuthStateInCookie: false },
  });
  await instance.initialize();
  return instance;
}

/**
 * The shared MSAL instance, initialised on first use. Concurrent callers await
 * the same initialisation; a failed one is dropped so the next call retries
 * instead of inheriting a poisoned promise.
 */
function getMsalInstance(): Promise<PublicClientApplication> {
  if (msalReady !== null) return msalReady;
  const ready = createMsalInstance();
  msalReady = ready;
  ready.catch(() => {
    if (msalReady === ready) msalReady = null;
  });
  return ready;
}

export async function signInWithMicrosoft(): Promise<void> {
  const instance = await getMsalInstance();
  const result = await instance.loginPopup({ scopes: MSAL_SCOPES });
  instance.setActiveAccount(result.account);
  const apiAccessToken = await acquireApiAccessToken(instance, result.account);
  await exchangeForPlatformJwt(
    'entra',
    result.idToken,
    undefined,
    apiAccessToken,
  );
}

/**
 * The access token for the sign-in app's own API (`sso.apiScope`), which identity
 * trades on the server for a Graph token. Strictly optional: with no scope
 * configured nothing is asked, and any failure — consent missing, interaction
 * required, a network blip — yields `undefined` so sign-in goes on exactly as
 * before. It never opens a popup, and the token itself is never logged.
 */
async function acquireApiAccessToken(
  instance: PublicClientApplication,
  account: AccountInfo,
): Promise<string | undefined> {
  const { apiScope } = getRuntimeConfig().sso;
  if (!apiScope) return undefined;
  try {
    const result = await instance.acquireTokenSilent({
      scopes: [apiScope],
      account,
    });
    return result.accessToken || undefined;
  } catch {
    logger.debug(
      'sign-in: no access token for the API scope, continuing without it',
    );
    return undefined;
  }
}

/**
 * Makes sure "SSO is off" is the server's answer before renewal acts on it. A
 * boot whose `/v1/config` fetch failed runs on a fallback that says SSO is off,
 * so while a session is held the config is asked for once more; only a server
 * that really has no SSO makes the session one nothing can renew (the local
 * email sign-in). A fetch that fails again is a passing failure, like any other
 * network error.
 */
async function ensureSsoConfigKnown(): Promise<void> {
  if (ssoConfigured()) return;
  const session = getState().platformJwt;
  if (session !== null && session.length > 0 && !runtimeConfigFetched()) {
    await loadRuntimeConfig(true);
    if (ssoConfigured()) return;
    if (!runtimeConfigFetched()) {
      throw new Error('Runtime config is unavailable');
    }
  }
  throw new SessionEndedError('Microsoft SSO is not configured');
}

/**
 * Standalone-browser renewal: ask Entra for a new ID token for the signed-in
 * account and exchange it for a fresh platform JWT.
 *
 * `forceRefresh` is what makes this worth doing — without it MSAL answers from
 * its own token cache, which can be an hour old, and the platform JWT would be
 * minted from stale directory claims (group membership among them). With it,
 * MSAL redeems the refresh token at Entra, so the claims are current as of now.
 *
 * Throws when nobody is signed in or Entra wants interaction; the caller treats
 * that as "keep the current token", never as a hard failure.
 */
async function renewViaEntra(): Promise<void> {
  await ensureSsoConfigKnown();
  const instance = await getMsalInstance();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (account === undefined || account === null) {
    throw new SessionEndedError('No signed-in Entra account to renew from');
  }
  const result = await instance.acquireTokenSilent({
    account,
    scopes: MSAL_SCOPES,
    forceRefresh: true,
  });
  const apiAccessToken = await acquireApiAccessToken(instance, account);
  await exchangeForPlatformJwt(
    'entra',
    result.idToken,
    undefined,
    apiAccessToken,
  );
}

/** Email sign-in (dev / when SSO is not configured) — identity issues a platform JWT. */
export async function signInWithEmail(email: string): Promise<void> {
  await exchangeForPlatformJwt('dev', undefined, email);
}

/**
 * One-click SSO sign-in. With Entra configured (`VITE_MSAL_CLIENT_ID`) it runs the real Microsoft
 * flow; in local/dev there's no SSO, so it signs in via the dev grant. To make it easy to test
 * sharing features as different users locally, the dev path first prompts for an email — leaving
 * the prompt blank (or cancelling) falls back to `VITE_DEV_SSO_EMAIL` (default `admin@cti.local`).
 * Same platform-JWT result either way.
 */
export async function signInWithSso(): Promise<void> {
  if (ssoConfigured()) {
    await signInWithMicrosoft();
    return;
  }
  const fallbackEmail =
    (import.meta.env['VITE_DEV_SSO_EMAIL'] as string | undefined)?.trim() ||
    'admin@cti.local';
  const entered = window
    .prompt(
      `Dev sign-in — enter an email to sign in as (lets you test sharing features across different users).\n\nLeave blank to sign in as ${fallbackEmail}.`,
    )
    ?.trim();
  await signInWithEmail(entered || fallbackEmail);
}

/**
 * Single-flight guard: AuthGate's StrictMode double-effect and concurrent
 * 401-refreshes (`ensureFreshPlatformJwt`) share one SDK round-trip + exchange
 * instead of racing duplicate `/v1/auth/token` calls.
 */
let teamsSsoInFlight: Promise<PlatformSession> | null = null;

/**
 * Teams Tab sign-in: exchange the tab's Teams SSO token for a platform JWT.
 *
 * `getAuthToken()` is non-interactive — Teams mints a token for the Entra app
 * in the manifest's `webApplicationInfo` (scope `access_as_user`); identity
 * validates it against Entra's JWKS (`ENTRA_*` config) and issues the platform
 * JWT. Both SDK calls are raced against a timeout: they hang outside Teams.
 */
export function signInWithTeamsSso(): Promise<PlatformSession> {
  if (teamsSsoInFlight !== null) return teamsSsoInFlight;
  const flight = (async (): Promise<PlatformSession> => {
    try {
      await withTimeout(
        app.initialize(),
        TEAMS_SSO_TIMEOUT_MS,
        'Teams app.initialize',
      );
      const teamsToken = await withTimeout(
        authentication.getAuthToken(),
        TEAMS_SSO_TIMEOUT_MS,
        'Teams getAuthToken',
      );
      if (!teamsToken) {
        throw new Error('Teams returned an empty SSO token');
      }
      return await exchangeForPlatformJwt('teams_sso', teamsToken);
    } finally {
      teamsSsoInFlight = null;
    }
  })();
  teamsSsoInFlight = flight;
  return flight;
}

/**
 * Raw teams_sso exchange for callers that need the session payload itself
 * (AdminFrame hands it to the embedded admin SPA via postMessage). Also
 * stores the platform JWT + identity, which is idempotent for the main app.
 */
export function exchangeTeamsSsoToken(
  teamsToken: string,
): Promise<PlatformSession> {
  return exchangeForPlatformJwt('teams_sso', teamsToken);
}

/**
 * A token within this window of `exp` counts as stale so a request in flight
 * doesn't lose the race against expiry. Identity's JWT_TTL is 15 min — short on
 * purpose, because it is also the revocation window — so 2 min is the headroom:
 * enough for one silent renewal round-trip to Entra plus clock drift between
 * the browser and identity, while still using ~87% of every token's life.
 */
const EXPIRY_SKEW_MS = 120_000;

/**
 * Best-effort freshness check on the platform JWT's `exp` claim: fresh means it
 * stays valid for more than `minValidityMs` from `now`. A token we cannot decode
 * (or one without `exp`) counts as fresh — the server is the authority and will
 * 401 if it isn't; we just avoid re-auth thrash.
 */
export function isPlatformJwtFresh(
  jwt: string,
  now = serverNow(),
  minValidityMs = EXPIRY_SKEW_MS,
): boolean {
  const claims = decodeJwtPayload(jwt);
  const exp = claims !== null ? claims['exp'] : undefined;
  if (typeof exp !== 'number') return true;
  return exp * 1000 - minValidityMs > now;
}

/**
 * Decode a JWT's payload segment to a plain claims object without verifying the
 * signature. Returns null for a malformed token. The server is the authority on
 * validity; callers use this only to read advisory claims (`exp`, `email`).
 */
export function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const segment = jwt.split('.')[1];
  if (segment === undefined || segment.length === 0) return null;
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * After a failed silent renewal, don't retry for this long. Without the
 * cooldown a broken SSO setup would tax every API call with the full SDK
 * timeout (8 s) instead of failing fast. Short (10 s) because the token it
 * guards only lives 15 min: while renewal is failing the session is already
 * expired, so every second of cooldown is a second of 401s. It still collapses
 * the burst of parallel calls a page load fires into a single attempt.
 */
const REFRESH_FAILURE_COOLDOWN_MS = 10_000;
let lastRefreshFailureAt = 0;

/**
 * How long renewal may keep failing before the session is called over. A
 * laptop waking onto a half-up network, or a Teams client slow to answer after
 * a resume, fails its first renewals and recovers seconds later; signing the
 * person out for that would throw away everything they had open. Still failing
 * after this long, the sign-in itself is gone and the login screen is honest.
 */
const RENEWAL_GIVE_UP_AFTER_MS = 120_000;

/** When the current unbroken run of failed renewals started, or 0 while renewal works. */
let renewalFailingSince = 0;

/** When the last renewal failed, to tell one run of failures from the next. */
let lastRenewalFailedAt = 0;

/**
 * A pause between two failed renewals longer than this starts a new run of
 * failures instead of continuing the old one. A laptop that slept between them
 * gets the full give-up window again on wake, rather than losing the session at
 * the first hiccup because the nap counted as failing time.
 */
const FAILURE_RUN_GAP_MS = 90_000;

/** Whether the last renewal failed in a way only a new sign-in can fix. */
let lastRenewalEndedSession = false;

/**
 * A renewal that cannot succeed without the person: there is no SSO to renew
 * from (the local email sign-in), MSAL holds no account, or identity refused
 * the credential outright. Entra asking for interaction is the same verdict and
 * arrives as MSAL's own `InteractionRequiredAuthError`. Every other failure — a
 * timeout, a network error, a 5xx — is treated as passing.
 */
class SessionEndedError extends Error {}

/** Whether a renewal failure is one only a new sign-in can fix. */
function endsSession(err: unknown): boolean {
  return (
    err instanceof SessionEndedError ||
    (err instanceof Error && err.name === 'InteractionRequiredAuthError')
  );
}

/**
 * Whether renewal has given up on this session: its last failure needs a new
 * sign-in, or one unbroken run of failures has lasted
 * {@link RENEWAL_GIVE_UP_AFTER_MS}. The run is measured between failures, not
 * up to now, so time spent asleep after a failure never counts. Until
 * then a rejected request is retried rather than ending the session.
 */
export function renewalHasGivenUp(): boolean {
  if (lastRenewalEndedSession) return true;
  return (
    renewalFailingSince !== 0 &&
    lastRenewalFailedAt - renewalFailingSince >= RENEWAL_GIVE_UP_AFTER_MS
  );
}

/**
 * Single-flight guard around renewal, both hosts: the 401-refresh path is hit
 * by many concurrent callers, and they share one round-trip instead of racing
 * duplicate `/v1/auth/token` calls.
 */
let renewalInFlight: Promise<void> | null = null;

/**
 * Renew the platform JWT through whichever path this host offers: Teams SSO in
 * a Tab (non-interactive), Entra silent acquisition in a standalone browser.
 *
 * Renewal deliberately goes back to the identity provider rather than through a
 * refresh-token grant of our own: a refresh grant never contacts Entra, so it
 * would keep re-minting a session from directory claims frozen at sign-in.
 */
function renewPlatformJwt(): Promise<void> {
  if (renewalInFlight !== null) return renewalInFlight;
  const flight = (async (): Promise<void> => {
    if (isRunningInTeams()) {
      await signInWithTeamsSso();
      return;
    }
    await renewViaEntra();
  })()
    .then(
      () => {
        renewalFailingSince = 0;
        lastRenewalEndedSession = false;
      },
      (err: unknown) => {
        const failedAt = Date.now();
        if (
          renewalFailingSince === 0 ||
          failedAt - lastRenewalFailedAt > FAILURE_RUN_GAP_MS
        )
          renewalFailingSince = failedAt;
        lastRenewalFailedAt = failedAt;
        lastRenewalEndedSession = endsSession(err);
        throw err;
      },
    )
    .finally(() => {
      renewalInFlight = null;
    });
  renewalInFlight = flight;
  return flight;
}

/**
 * Returns the platform JWT to attach as `Bearer`, silently re-acquiring it
 * first when it is missing or close to expiry. Host-agnostic: a Teams Tab
 * re-exchanges its Teams SSO token, a standalone browser renews against Entra
 * with the account MSAL holds. Renewal failure is never fatal — the current
 * token is returned and the resulting 401 is the login screen's cue.
 *
 * Once the gate has denied this account, renewal stops here: no Teams SDK call,
 * no Entra round-trip, no exchange. The JWT already issued lives out its 15
 * minutes, exactly as the gate's revocation window promises — and when it is
 * done, so is the session.
 *
 * While Teams has the tab suspended, a stale token waits for the resume: no SDK
 * call is allowed then, and a renewal that failed for that reason would arm the
 * cooldown and leave the woken tab on 401s.
 *
 * `minValidityMs` raises the bar for a caller whose token must outlive the
 * request itself, such as a send that starts an agent turn.
 */
export async function ensureFreshPlatformJwt(
  minValidityMs = EXPIRY_SKEW_MS,
): Promise<string | null> {
  const current = getState().platformJwt;
  if (
    current !== null &&
    current.length > 0 &&
    isPlatformJwtFresh(current, serverNow(), minValidityMs)
  ) {
    return current;
  }
  if (accessDenied !== null) {
    if (current !== null && current.length > 0 && isPlatformJwtFresh(current))
      return current;
    // Reached only past the token's life — the fresh path returned above. Holding
    // a dead token here would leave the app rendering while every call answers
    // 401 and the denial screen never appears, because AuthGate reads the denial
    // off a cleared session. So this is where the refused session ends.
    if (current !== null) setPlatformJwt(null);
    return null;
  }
  if (isTeamsAppSuspended()) {
    await whenTeamsResumed();
    return ensureFreshPlatformJwt(minValidityMs);
  }
  if (Date.now() - lastRefreshFailureAt < REFRESH_FAILURE_COOLDOWN_MS) {
    return current;
  }
  try {
    await renewPlatformJwt();
    lastRefreshFailureAt = 0;
  } catch (err: unknown) {
    lastRefreshFailureAt = Date.now();
    logger.warn('ensureFreshPlatformJwt: silent renewal failed', err);
  }
  return getState().platformJwt;
}

/**
 * The last token a rejection-driven renewal was already spent on. One forced
 * round-trip per rejected token is the rule: a burst of 401s carrying the same
 * dead token must not become a burst of Teams SDK calls (8 s each) or Entra
 * round-trips, and a time window would be guesswork where the token identity is
 * exact.
 */
let renewalTriedForToken: string | null = null;

/**
 * Renewal for a caller whose request the **server** just rejected with 401.
 *
 * Distinct from `ensureFreshPlatformJwt` because both of that function's
 * short-circuits are wrong here. `exp` says nothing about a token identity has
 * stopped accepting — a rotated signing key or a server-side invalidation leaves
 * a perfectly unexpired token being refused — so the freshness check would
 * return the same dead token and no caller would ever re-exchange. And the
 * failure cooldown exists to stop a broken SSO setup from taxing every proactive
 * call; applied here it would swallow the one renewal a 401 actually justifies,
 * because the proactive attempt that just failed armed it.
 *
 * What is kept: a latched denial still stops everything (no round-trip once the
 * gate has said no), concurrent callers still share one exchange, and one
 * rejected token buys at most one forced attempt. A rejection that arrives while
 * Teams has the tab suspended waits for the resume, so that one attempt is not
 * spent where the SDK cannot answer.
 */
export async function renewAfterRejection(
  rejected: string | null,
): Promise<string | null> {
  if (accessDenied !== null) return endRefusedSession();
  if (isTeamsAppSuspended()) {
    await whenTeamsResumed();
    return renewAfterRejection(rejected);
  }
  const current = getState().platformJwt;
  // Another caller already replaced the rejected token — nothing to renew.
  if (current !== null && current.length > 0 && current !== rejected)
    return current;
  const alreadyTried = rejected !== null && rejected === renewalTriedForToken;
  if (alreadyTried && renewalInFlight === null) return current;
  renewalTriedForToken = rejected;
  try {
    await renewPlatformJwt();
    lastRefreshFailureAt = 0;
  } catch (err: unknown) {
    lastRefreshFailureAt = Date.now();
    logger.warn(
      'renewAfterRejection: renewal after a rejected token failed',
      err,
    );
  }
  // The refusal may be what this very renewal discovered.
  if (accessDenied !== null) return endRefusedSession();
  return getState().platformJwt;
}

/**
 * Ends a session the gate has refused. Only ever reached from the rejection
 * path, where the server has already turned the token away — so this takes
 * nothing from anyone that still worked, and §7's promise that an issued JWT
 * lives out its 15 minutes is untouched: that promise covers a token the server
 * still accepts, which by definition this is not.
 *
 * Ending it here is what makes the denial visible. `exp` cannot be the trigger:
 * a token the server rejects while its own expiry claim still looks healthy
 * would otherwise be handed back to every caller, leaving the app rendering on
 * 401s with the denial screen hidden until the token aged out.
 */
function endRefusedSession(): null {
  if (getState().platformJwt !== null) setPlatformJwt(null);
  return null;
}

/** How often a share link asks again while renewal is failing for a passing reason. */
const OPTIONAL_SESSION_RETRY_MS = 5_000;

/**
 * The bearer for a request whose caller may have no session at all, such as a
 * share link: nothing when nobody is signed in on this browser, so an outside
 * viewer never triggers a sign-in, and a renewed token when somebody is, so a
 * signed-in person opening a link after a quiet quarter of an hour is not
 * mistaken for a stranger. While renewal fails for a passing reason it keeps
 * asking, until renewal succeeds or gives up — at most
 * {@link RENEWAL_GIVE_UP_AFTER_MS} after the failures began — so a link never
 * reads "sign in" to a person whose session is only slow to renew. Once renewal
 * gives up it hands back the stored token.
 */
export async function optionalSessionJwt(): Promise<string | null> {
  const stored = getState().platformJwt;
  if (stored === null || stored.length === 0) return null;
  const deadline =
    Date.now() + RENEWAL_GIVE_UP_AFTER_MS + OPTIONAL_SESSION_RETRY_MS;
  for (;;) {
    const renewal = ensureFreshPlatformJwt();
    const token =
      (await Promise.race([renewal, nullAfter(deadline - Date.now())])) ??
      getState().platformJwt ??
      stored;
    if (
      isPlatformJwtFresh(token, undefined, 0) ||
      renewalHasGivenUp() ||
      accessDenied !== null ||
      Date.now() >= deadline
    )
      return token;
    await new Promise((resolve) =>
      setTimeout(resolve, OPTIONAL_SESSION_RETRY_MS),
    );
  }
}

/**
 * Resolves to null after `ms`, so a caller can stop waiting on a renewal that
 * hangs without cancelling it for everyone else who shares it.
 */
function nullAfter(ms: number): Promise<null> {
  return new Promise((resolve) =>
    setTimeout(() => resolve(null), Math.max(ms, 0)),
  );
}

/** Whether the access gate has already refused this account in this page's life. */
export function isAccessDenied(): boolean {
  return accessDenied !== null;
}

/** Test-only: resets module state (in-flight exchanges, failure cooldown, denial, MSAL instance). */
export function _resetSsoForTests(): void {
  teamsSsoInFlight = null;
  renewalInFlight = null;
  lastRefreshFailureAt = 0;
  renewalTriedForToken = null;
  accessDenied = null;
  msalReady = null;
  renewalFailingSince = 0;
  lastRenewalFailedAt = 0;
  lastRenewalEndedSession = false;
  _resetServerClockForTests();
}

/**
 * Grant types identity accepts on `POST /v1/auth/token`.
 */
type GrantType = 'entra' | 'dev' | 'teams_sso';

/**
 * Is this failed exchange a refusal of the person rather than a failure of the
 * sign-in?
 *
 * Identity says so itself in `error_code`, and that is what decides: the status
 * cannot. It answers 403 both when the gate refuses an account and when email
 * sign-in is switched off, and it answers 401 for an account that has been
 * suspended or deleted — a refusal wearing the status of a broken credential.
 *
 * Without a code the answer is the old one, so a client newer than the server
 * behaves exactly as it did: a 403 on an SSO grant is a denial, and a 403 on the
 * dev grant is the switched-off email grant. An unfamiliar code from a newer
 * server falls back to the same rule.
 */
function isAccessRefusal(
  status: number,
  grant: GrantType,
  errorCode: string,
): boolean {
  if (errorCode === 'access_denied' || errorCode === 'account_inactive')
    return true;
  if (errorCode === 'email_grant_disabled') return false;
  return status === 403 && grant !== 'dev';
}

/**
 * The one funnel every sign-in and every renewal goes through, whatever the
 * host and whatever the grant — so it is also the one place that can tell a
 * refusal from a failure. Everything that is not a refusal stays an ordinary
 * error the caller may retry.
 */
async function exchangeForPlatformJwt(
  grant: GrantType,
  token?: string,
  email?: string,
  apiAccessToken?: string,
): Promise<PlatformSession> {
  if (accessDenied !== null) throw accessDenied;
  const body =
    grant === 'dev'
      ? { grant_type: 'dev', email }
      : apiAccessToken
        ? { grant_type: grant, token, api_access_token: apiAccessToken }
        : { grant_type: grant, token };
  const requestStartedAt = Date.now();
  const res = await fetch('/v1/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const { detail, errorCode } = await readErrorBody(res);
    if (isAccessRefusal(res.status, grant, errorCode)) {
      logger.warn(
        `sign-in exchange (${grant}) denied${detail ? ` — ${detail}` : ''}`,
      );
      accessDenied = new AccessDeniedError();
      throw accessDenied;
    }
    logger.warn(
      `sign-in exchange (${grant}) failed: ${res.status}${detail ? ` — ${detail}` : ''}`,
    );
    const message = detail || `Sign-in failed (${res.status})`;
    throw res.status === 401
      ? new SessionEndedError(message)
      : new Error(message);
  }
  const data = (await res.json()) as Partial<PlatformSession>;
  if (
    typeof data.token !== 'string' ||
    data.token.length === 0 ||
    data.user === undefined
  ) {
    throw new Error('Sign-in exchange returned a malformed session payload');
  }
  rememberServerClock(data.token, requestStartedAt);
  renewalFailingSince = 0;
  lastRenewalEndedSession = false;
  setPlatformJwt(data.token);
  const u = data.user;
  setIdentity({
    userId: u.id,
    tenantId: u.tenant_id ?? 'cti',
    displayName: u.name ?? u.email,
    roles: u.roles ?? [],
    graphSearchNoticeRequired: u.graph_search_notice_required === true,
  });
  try {
    localStorage.setItem('ct-user-id', u.id);
  } catch {
    /* storage unavailable */
  }
  return data as PlatformSession;
}

/** What a failed exchange tells us, read from the body in one pass. */
interface ExchangeError {
  /** Nest's `{ message }` — the exact reason (e.g. "Teams SSO exchange is not
   *  configured") to log or show instead of a bare status. */
  detail: string;
  /** Identity's machine-readable `error_code`, empty when the body carries none. */
  errorCode: string;
}

/**
 * Reads the error body once: a `Response` body can only be consumed one time,
 * and both the reason and the code come out of the same JSON.
 */
async function readErrorBody(res: Response): Promise<ExchangeError> {
  try {
    const body = (await res.json()) as {
      message?: string | string[];
      error_code?: unknown;
    };
    const detail = Array.isArray(body.message)
      ? body.message.join('; ')
      : typeof body.message === 'string'
        ? body.message
        : '';
    return {
      detail,
      errorCode: typeof body.error_code === 'string' ? body.error_code : '',
    };
  } catch {
    return { detail: '', errorCode: '' };
  }
}
